import {
  deleteTripPhoto,
  getTripPhotos,
  uploadTripPhotos,
} from "api/booking/booking";
import { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";

interface FileWithPreview extends File {
  preview: string;
}

interface UploadedImage {
  id: number;
  url: string;
}

export default function UploadZone({
  bookingId,
  preTrip,
}: {
  bookingId: any;
  preTrip: boolean;
}) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploadedImages, setUploadedImages] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllImages, setShowAllImages] = useState(false); // Track the show/collapse state
  const IMAGES_TO_SHOW = 4; // Limit of initially visible images

  // Fetch existing trip photos when the component loads
  const fetchPhotos = async () => {
    try {
      const photos = await getTripPhotos(bookingId);

      if (Array.isArray(photos)) {
        setUploadedImages(photos); // Set photos if response is an array
      } else {
        throw new Error(photos?.error || "Unexpected response format"); // Handle non-array response
      }
    } catch (error) {
      console.error("Failed to fetch trip photos:", error);
      // toast.error("Failed to fetch trip photos");
      setUploadedImages([]); // Set an empty array to prevent rendering issues
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [bookingId]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ),
    ]);
  }, []);

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const handleRemoveUploadedImage = async (id: number) => {
    try {
      await deleteTripPhoto(id);
      setUploadedImages((prevImages: any) =>
        prevImages.filter((img: any) => img.id !== id)
      );
      toast.success("Photo deleted successfully");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const startUpload = async () => {
    setIsLoading(true);
    try {
      const response = await uploadTripPhotos(files, bookingId, preTrip);

      if (response.error) {
        throw new Error(response.error);
      }

      const newPhotos = response.photos || []; // Assuming photos is the array of new photos
      if (!Array.isArray(newPhotos)) {
        throw new Error("Invalid response format: expected an array of photos");
      }

      // Append new photos to the state
      setUploadedImages((prevImages: any) => [...prevImages, ...newPhotos]);
      setFiles([]); // Clear files after successful upload
      toast.success("Files uploaded successfully");
      fetchPhotos();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
  });

  const displayedImages = showAllImages
    ? uploadedImages
    : uploadedImages.slice(0, IMAGES_TO_SHOW);

  function formatDate(dateString: string) {
    const date = new Date(dateString);

    // Correct options for formatting the date
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    // Get the formatted date
    const formattedDate = date.toLocaleString("en-GB", options);

    return formattedDate;
  }

  return (
    <div className="  rounded-md">
      <div className="rounded-lg">
        <div
          {...getRootProps()}
          className="cursor-pointer p-4 border-2 border-dotted border-gray-300 rounded-md"
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center min-h-20">
            {/* Upload Icon */}
            <svg
              width="64"
              height="55"
              viewBox="0 0 64 55"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <rect
                x="0.304688"
                y="0.0205078"
                width="63.389"
                height="54.1388"
                fill="url(#pattern0_2406_2739)"
              />
              <defs>
                <pattern
                  id="pattern0_2406_2739"
                  patternContentUnits="objectBoundingBox"
                  width="1"
                  height="1"
                >
                  <use
                    xlinkHref="#image0_2406_2739"
                    transform="matrix(0.00961538 0 0 0.0112583 0 -0.0741714)"
                  />
                </pattern>
                <image
                  id="image0_2406_2739"
                  width="104"
                  height="102"
                  xlinkHref="data:image/png;base64,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"
                />
              </defs>
            </svg>

            <p className="text-gray-500">Drop or select files</p>
          </div>
        </div>
        {!isLoading && files?.length > 0 && (
          <div>
            {/* Display images in a grid layout */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {files?.map((file, index) => (
                <div key={index} className="relative mt-4">
                  {file.type.startsWith("image") && (
                    <div className="group relative h-[115px] w-full rounded-md bg-gray-50">
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="rounded-md object-cover h-full w-full"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Clear and Upload buttons */}
            <div className="flex gap-4 mt-4">
              <button
                onClick={clearFiles}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 9.75L19.5 18.75C19.5 19.9926 18.4926 21 17.25 21L6.75 21C5.50736 21 4.5 19.9926 4.5 18.75L4.5 9.75M9.75 9.75V16.5M14.25 9.75V16.5M3 6.75H21M9.75 6.75V4.5C9.75 3.94772 10.1977 3.5 10.75 3.5H13.25C13.8023 3.5 14.25 3.94772 14.25 4.5V6.75"
                  />
                </svg>
                Clear {files.length} files
              </button>

              <button
                onClick={startUpload}
                className="bg-blue-500 text-white py-2 px-4 rounded-lg flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v3a1.5 1.5 0 001.5 1.5h15a1.5 1.5 0 001.5-1.5v-3M7.5 12l4.5-4.5m0 0L16.5 12m-4.5-4.5V21"
                  />
                </svg>
                Upload {files.length} files
              </button>
            </div>
          </div>
        )}

        {!files?.length && !uploadedImages?.length && (
          <p className="text-gray-400 mt-2">No files selected or uploaded</p>
        )}
      </div>

      {uploadedImages &&
        uploadedImages?.filter((image: any) =>
          preTrip
            ? image.image_type === "pre_trip"
            : image.image_type === "post_trip"
        ).length > 0 && (
          <>
            <h2 className="font-bold text-lg my-4">Uploaded Files</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {displayedImages
                .filter((image: any) =>
                  preTrip
                    ? image.image_type === "pre_trip"
                    : image.image_type === "post_trip"
                )
                .map((image: any) => (
                  <div key={image.id} className="relative">
                    <img
                      src={`${image.image}`}
                      alt={`Uploaded Image ${image.id}`}
                      className="rounded-md object-cover h-[150px] w-full"
                    />
                    <p className="mt-2 font-bold">
                      {image?.added_by?.first_name} {image?.added_by?.last_name}
                    </p>
                    <p className="text-gray-600 font-semibold">
                      {formatDate(image?.created_at)}
                    </p>
                  </div>
                ))}
            </div>
            {uploadedImages?.filter((image: any) =>
              preTrip
                ? image.image_type === "pre_trip"
                : image.image_type === "post_trip"
            ).length > IMAGES_TO_SHOW && (
              <div className="flex justify-center mt-4">
                <ButtonPrimary
                  onClick={() => setShowAllImages(!showAllImages)}
                  className="mt-4 text-white "
                >
                  {showAllImages ? "Show Less" : "Show More"}
                </ButtonPrimary>
              </div>
            )}
          </>
        )}

      {/* File Preview */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4"></div>

      {/* Upload and Clear Buttons */}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="flex justify-center mt-4">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-blue-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8"
      ></path>
    </svg>
  );
}
